import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "cardForm",
    "cardHost",
    "pmField",
    "siField",
    "piField",
    "submitBtn",
    "error",
    "needsSetup",
    "defaultCard",
    "stripeCardForm",
    "useDefaultLink"
  ];

  static values = {
    origin: String,
    orderId: String,
    clientSecret: String
  };

  /* -------------------- Lifecycle -------------------- */

  connect() {
    this.submitting = false;
    this.cardEmpty = true;
    this.cardComplete = false;
    this.cardTouched = false;

    const pk = document.querySelector("meta[name='stripe-key']");
    if (!pk || !window.Stripe) {
      this.setError("Payment system unavailable.");
      return;
    }
    const faitStripeAccount = this.cardHostTarget.dataset.stripeAccount;

    if(faitStripeAccount){
      this.stripe = Stripe(pk.content, { stripeAccount: faitStripeAccount });
    } else {
      this.stripe = Stripe(pk.content);
    }
this.needsConfirmation
    this.elements = this.stripe.elements();

    if (this.hasCardHostTarget && !this.card) {
      this.card = this.elements.create("card");

      this.card.on("change", (event) => {
        this.cardEmpty = event.empty;
        this.cardComplete = event.complete;
        if (!event.empty) this.cardTouched = true;
      });

      this.card.mount(this.cardHostTarget);
    }
  }

  /* -------------------- Submit -------------------- */

  async submitForm(e) {
    e.preventDefault();
    if (this.submitting) return;

    this.clearError();
    this.submitting = true;
    this.disableBtn();

    try {
      if (this.originValue === "payment" && this.cardComplete) {
        await this.handleSetup();
        await this.handlePayment();
      }
      else if (this.originValue === "payment" && !this.cardComplete) {
        await this.handlePayment();
      }
      else if (this.cardComplete) {
        await this.handleSetup();
      } else {
        throw new Error("Please enter your card details.");
      }

      this.form.submit();
    } catch (err) {
      if(err.refresh_payment_form) {
        this.refreshPaymentForm();
      }
      this.setError(err?.message || "Payment could not be completed.");
      this.enableBtn();
    } finally {
      this.submitting = false;
    }
  }

  /* -------------------- PaymentIntent -------------------- */

  async handlePayment() {
    if (!this.clientSecretValue) {
      await this.createPaymentIntent();
    }

    let { paymentIntent } =
      await this.stripe.retrievePaymentIntent(this.clientSecretValue);

    if (!paymentIntent) {
      throw new Error("Unable to retrieve payment state.");
    }

    // Saved card is invalid and user has not entered a new one
    if (
      paymentIntent.status === "requires_payment_method" &&
      !this.cardTouched
    ) {
      throw new Error("That card can’t be used. Please enter a new card.");
    }

    if (this.needsConfirmation(paymentIntent)) {
      await this.confirmPayment(paymentIntent);

      const result =
        await this.stripe.retrievePaymentIntent(this.clientSecretValue);

      paymentIntent = result.paymentIntent;
    }
    
    if (paymentIntent.status === "requires_action") {
      throw new Error("Additional authentication is required. Please try again.");
    }

    const okStates = ["requires_capture", "processing", "succeeded"];
    if (!okStates.includes(paymentIntent.status)) {
      throw new Error(
        "Payment could not be completed. Please try another card."
      );
    }

    this.setHiddenIds(paymentIntent);
  }

  needsConfirmation(paymentIntent) {
    return (
      paymentIntent.status === "requires_confirmation" ||
      paymentIntent.status === "requires_action" ||
      (
        paymentIntent.status === "requires_payment_method" &&
        this.cardTouched
      )
    );
  }

  async confirmPayment(paymentIntent) {
    const params = { return_url: window.location.href };

    // Default / saved card
    if (
      paymentIntent.payment_method &&
      paymentIntent.status !== "requires_payment_method"
    ) {
      const { error } =
        await this.stripe.confirmCardPayment(
          this.clientSecretValue,
          params
        );

      if (error) throw new Error(error.message);
      return;
    }

    // New card
    if (!this.cardComplete) {
      throw new Error("Please enter your card details.");
    }

    const { error } =
      await this.stripe.confirmCardPayment(this.clientSecretValue, {
        ...params,
        payment_method: { card: this.card }
      });

    if (error) throw new Error(error.message);
  }

  /* -------------------- SetupIntent -------------------- */

  async handleSetup() {
    if (this.cardComplete || this.needsSetupTarget.value === "true") {
      const clientSecret = await this.createSetupIntent();
      const { error, setupIntent } =
          await this.stripe.confirmCardSetup(clientSecret, {
            payment_method: { card: this.card },
            return_url: window.location.href
          });
      if (error) throw new Error(error.message);
      if (!setupIntent) {
        throw new Error("Authentication required. Please complete verification.");
      }

      this.paymentMethodId = setupIntent.payment_method;
      this.setupIntentId = setupIntent.id;

      if (setupIntent.status !== "succeeded") {
        throw new Error("Card setup could not be completed. Please try again.");
      }

      if (this.hasPmFieldTarget) {
        this.pmFieldTarget.value = setupIntent.payment_method;
      }
      if (this.hasSiFieldTarget) {
        this.siFieldTarget.value = setupIntent.id;
      }
    }
  }

  /* -------------------- API Calls -------------------- */

  async createPaymentIntent() {
    let res;
    try{
      res = await fetch("/payment_intent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrf
        },
        body: JSON.stringify({
          order_id: this.orderIdValue,
          payment_method_id: this.paymentMethodId ?? null,
          setup_intent_id: this.setupIntentId ?? null
        })
      });
    } catch (e) {
      console.error(e)
      throw new Error("Network error. Please check your connection.");
    }
    let data;
    try {
      data = await res.json();
    } catch (e) {
      console.error(`PaymentIntent fetch failed:: ${e}`)
      throw new Error("Invalid server response.");
    }
    if (!res.ok) {
      throw data;
    }

    const { client_secret, pi_id } = data
    this.clientSecretValue = client_secret;

    if (this.hasPiFieldTarget) {
      this.piFieldTarget.value = pi_id;
    }
  }

  async createSetupIntent() {
    let res;
    try {
      res = await fetch("/setup_intents", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrf
        }
      });
    } catch (e) {
      console.error(e)
      throw new Error("Network error. Please check your connection.");
    }
    
    let data;
    try {
      data = await res.json();
    } catch (e) {
      console.error(`SetupIntent fetch failed:: ${e}`)
      throw new Error("Invalid server response.");
    }

    if (!res.ok) {
      throw new Error(data?.error || "Unable to create setup intent.");
    }

    return data.client_secret;
  }

  /* -------------------- UI helpers -------------------- */

  disableBtn() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true;
    }
  }

  enableBtn() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = false;
    }
  }

  setError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = "";
    }
  }

  setHiddenIds(paymentIntent) {
    if (this.hasPiFieldTarget) {
      this.piFieldTarget.value = paymentIntent.id;
    }
    if (this.hasPmFieldTarget && paymentIntent.payment_method) {
      this.pmFieldTarget.value = paymentIntent.payment_method;
    }
  }

  get form() {
    if (this.hasCardFormTarget) {
      return this.cardFormTarget;
    }
    const form = this.element.querySelector("form");
    if (form) return form;
    throw new Error("No form found to submit.");
  }

  get csrf() {
    return document
      .querySelector("meta[name='csrf-token']")
      ?.getAttribute("content");
  }

  refreshPaymentForm() {
    this.resetPaymentState();
    this.showStripeCardForm();
  }

  resetPaymentState() {
    this.clientSecretValue = '';
    this.cardTouched = false;
    this.cardComplete = false;
    this.paymentMethodId = null;
    this.setupIntentId = null;
  }

  showStripeCardForm() {
    this.defaultCardTarget.classList.add('hidden');
    this.stripeCardFormTarget.classList.remove('hidden');
    this.useDefaultLinkTarget.classList.add('hidden');
  }
}
